import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Network, Database, Cpu, Layers, AlertTriangle, Lock, Search } from 'lucide-react';
import InteractiveBackground from '@/components/ui/InteractiveBackground';
import { ReadingHeader } from '@/components/gol/ReadingHeader';
import { ReadingNext } from '@/components/gol/ReadingNext';

const KnowledgeSystems = () => {
  return (
    <div data-testid="knowledge-page" className="reading-page min-h-screen bg-abyss-950 text-sand-50 font-sans selection:bg-teal-400/30 overflow-x-hidden relative">
      <InteractiveBackground />

      <div className="relative z-10 pt-32 pb-24 px-6">
        <div className="max-w-4xl mx-auto space-y-24">
            
            {/* Header */}
            <ReadingHeader id="knowledge" category="Graph intelligence" title="Evidence that stays connected to its meaning." intro="A knowledge graph holds relationships together with their sources and uncertainty, giving scientists, institutions and AI a shared foundation for examining ecological questions." />

            {/* The Problem */}
            <div className="grid md:grid-cols-12 gap-12 border-t border-abyss-800 pt-12">
                <div className="md:col-span-4">
                    <h2 className="text-sm font-mono uppercase tracking-widest text-teal-400/80 sticky top-32">Data Fragmentation</h2>
                </div>
                <div className="md:col-span-8 prose prose-invert prose-lg text-sand-50/80 font-light leading-relaxed">
                    <p>
                        Ecological knowledge lives in papers, siloed databases, field notes and expert intuition. Critical relationships between species, habitats and stress responses stay outside any machine-readable form.
                    </p>
                    <p>
                        Every new investigation has to reconstruct those connections from scratch. A governed graph makes that work reusable, with its sources and assumptions always open to review.
                    </p>
                </div>
            </div>

            {/* WHY THIS MATTERS IN THE AI ERA */}
            <div className="grid md:grid-cols-12 gap-12 border-t border-abyss-800 pt-12 bg-teal-900/5 -mx-6 px-6 pb-12">
                <div className="md:col-span-4">
                    <h2 className="text-sm font-mono uppercase tracking-widest text-teal-400/80 sticky top-32 flex items-center gap-2">
                        <Cpu size={14} />
                        Accountable reasoning
                    </h2>
                </div>
                <div className="md:col-span-8 space-y-8">
                    <h3 className="text-xl font-serif text-sand-50">Give AI an evidence chain it can follow.</h3>
                    <p className="text-lg text-sand-50/80 font-light leading-relaxed">
                        Useful reasoning depends on knowing what a relationship means, where it came from and how far it can carry an inference. That is what <strong className="text-teal-400 font-normal">governed knowledge architecture</strong> does.
                    </p>
                    
                    <div className="space-y-4">
                        <AIRow 
                            icon={Search}
                            text="AI can query trophic relationships once those relationships exist in queryable form."
                        />
                        <AIRow 
                            icon={AlertTriangle}
                            text="Uncertainty remains attached to each inferred relationship and its supporting observations."
                        />
                        <AIRow 
                            icon={Lock}
                            text="Domain owners review the assumptions linking ecological signals to institutional questions."
                        />
                    </div>

                    <div className="p-6 border-l-2 border-teal-500 bg-abyss-900/50 italic text-sand-50/90 font-serif text-lg leading-relaxed">
                        The reasoning remains useful when another person can follow the evidence, challenge an assumption and understand what changes.
                    </div>
                </div>
            </div>

            {/* The Solution: Knowledge Graphs */}
            <div className="grid md:grid-cols-12 gap-12 border-t border-abyss-800 pt-12">
                <div className="md:col-span-4">
                    <h2 className="text-sm font-mono uppercase tracking-widest text-teal-400/80 sticky top-32">Graph Architecture</h2>
                </div>
                <div className="md:col-span-8 space-y-12">
                    <p className="text-lg text-sand-50/80 font-light">
                        I structure ecological relationships into queryable infrastructure spanning entities, their connections and the attributes that describe each one.
                    </p>
                    
                    <div className="space-y-4">
                        <TechRow 
                            icon={Database}
                            title="Entities"
                            desc="Species, habitats, chemical stressors, ecosystem services, economic assets."
                        />
                         <TechRow 
                            icon={Network}
                            title="Relationships"
                            desc="Trophic links, habitat associations and functional dependencies, with observed and inferred relationships distinguished."
                        />
                         <TechRow 
                            icon={Layers}
                            title="Attributes"
                            desc="Uncertainty bounds, provenance (source paper/dataset), temporal validity, spatial resolution."
                        />
                    </div>

                    <div data-testid="knowledge-query-example" className="p-6 bg-teal-900/10 border border-teal-500/20 rounded-sm text-sm text-teal-300 space-y-4">
                        <p className="font-mono text-[10px] uppercase text-sand-50/60">Illustrative research question</p>
                        <p>Which species relationships might connect habitat loss to this fishery?</p>
                        <p className="text-sand-50/70 leading-relaxed">An evidence trace would identify candidate pathways, their sources and the observations required to test them. A calibrated yield forecast requires separate validation.</p>
                    </div>
                </div>
            </div>

            {/* Reasoning Over Structure */}
            <div className="grid md:grid-cols-12 gap-12 border-t border-abyss-800 pt-12">
                <div className="md:col-span-4">
                    <h2 className="text-sm font-mono uppercase tracking-widest text-teal-400/80 sticky top-32">Reasoning Engines</h2>
                </div>
                <div className="md:col-span-8 space-y-8">
                     <p className="text-lg text-sand-50/80 font-light leading-relaxed">
                        <strong>Retrieval-augmented reasoning</strong> draws on the graph's relationships and evidence records. Each of the directions below requires domain-specific testing before operational use.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="p-4 border-l border-abyss-700">
                            <h4 className="font-serif text-sand-50 mb-1">Diligence Preparation</h4>
                            <p className="text-sm text-sand-50/60">Organizing claims and evidence for a reviewer's investigation.</p>
                        </div>
                        <div className="p-4 border-l border-abyss-700">
                            <h4 className="font-serif text-sand-50 mb-1">Scenario Modeling</h4>
                            <p className="text-sm text-sand-50/60">Simulating policy interventions or climate shocks.</p>
                        </div>
                        <div className="p-4 border-l border-abyss-700">
                            <h4 className="font-serif text-sand-50 mb-1">Early Warning</h4>
                            <p className="text-sm text-sand-50/60">Investigating candidate indicators for monitoring and field validation.</p>
                        </div>
                        <div className="p-4 border-l border-abyss-700">
                            <h4 className="font-serif text-sand-50 mb-1">Biodiversity Disclosure</h4>
                            <p className="text-sm text-sand-50/60">Organizing source evidence against disclosure requirements.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Current Implementations */}
            <div className="border-t border-abyss-800 pt-12">
                <h3 className="text-xl font-serif text-sand-50 mb-6">Current Implementations</h3>
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-abyss-900/30 p-6 border border-abyss-800">
                        <div className="text-xs font-mono uppercase text-teal-500 mb-2">Marine Trophic Network</div>
                        <div className="text-2xl font-light mb-1">11,000+ Species</div>
                        <div className="text-sm text-sand-50/40">20M+ Relationships mapped</div>
                    </div>
                    <div className="bg-abyss-900/30 p-6 border border-abyss-800">
                        <div className="text-xs font-mono uppercase text-teal-500 mb-2">Ag-Knowledge System</div>
                        <div className="text-2xl font-light mb-1">Soil Health Graph</div>
                        <div className="text-sm text-sand-50/40">Linking practice to functional outcomes</div>
                    </div>
                </div>
            </div>

            <ReadingNext id="knowledge" to="/systems" title="Inspect the architecture in practice." copy="Working systems with published methods, evidence states and explicit limits." />
        </div>
      </div>
    </div>
  );
};

const TechRow = ({ icon: Icon, title, desc }) => (
    <div className="flex gap-4 items-start">
        <div className="mt-1 p-2 bg-abyss-800/50 rounded-sm">
            <Icon size={16} className="text-teal-400" />
        </div>
        <div>
            <h4 className="text-sand-50 font-medium mb-1">{title}</h4>
            <p className="text-sm text-sand-50/60">{desc}</p>
        </div>
    </div>
);

const AIRow = ({ icon: Icon, text }) => (
    <div className="flex gap-4 items-start group">
        <div className="mt-1 min-w-[24px]">
            <Icon size={18} className="text-teal-500/60 group-hover:text-teal-400 transition-colors" />
        </div>
        <p className="text-sm text-sand-50/70 font-light leading-relaxed group-hover:text-sand-50/90 transition-colors">
            {text}
        </p>
    </div>
);

export default KnowledgeSystems;
